import { Grid, useTheme, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import NavBar from '../components/NavBar';
import { useNavigate } from 'react-router';
import '../../index.css';
import { primaryColor, styles } from '../../assets/styles/styles';
import { Helmet } from 'react-helmet';
import { FadeInComponent } from '../components/FadeInComponent';

export default function WelshAthletics() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [scrollPosition, setScrollPosition] = useState(0);

  const handleScroll = () => {
    const position = window.scrollY;
    setScrollPosition(position);
  };

  const scrollToBenefits = () => {
    const benefitsSection = document.getElementById('benefits-section');
    if (benefitsSection) {
      benefitsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Helmet>
        <meta
          name="title"
          content="Welsh Athletics & RouteBuddies Partnership | Safe Spaces for Every Run Club"
        />
        <meta
          name="description"
          content="Discover how RouteBuddies and Welsh Athletics have partnered to bring safe spaces forward for every run club in Wales."
        />
        <meta
          name="keywords"
          content="Welsh Athletics, RouteBuddies, running clubs Wales, safe running spaces, athletics clubs, run club safety, Welsh runners, community running, athletics safety"
        />
        <meta name="robots" content="index, follow" />
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="language" content="English" />
        <meta name="author" content="RouteBuddies Limited." />
        <meta
          property="og:title"
          content="Welsh Athletics & RouteBuddies Partnership"
        />
        <meta
          property="og:description"
          content="Discover how RouteBuddies and Welsh Athletics have partnered to bring safe spaces forward for every run club in Wales."
        />
        <meta property="og:url" content="https://routebuddies.com/welsh-athletics" />
        <meta property="og:type" content="website" />
      </Helmet>

      <NavBar
        theme={theme}
        navigate={navigate}
        loggedIn={false}
        transparent={false}
      />

      <Grid
        container
        flexDirection={'column'}
        sx={{
          maxWidth: '100vw',
          backgroundColor: 'white',
        }}
      >
        {/* Hero Section */}
        <Grid
          container
          item
          xs={12}
          justifyContent={'center'}
          sx={{
            paddingTop: 15,
            paddingBottom: 10,
            backgroundColor: 'white',
          }}
        >
          <FadeInComponent scrollPosition={scrollPosition}>
            <Grid item container xs={12} justifyContent={'center'}>
              <Grid
                container
                item
                xs={11}
                md={12}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: '#fff3f1',
                  borderRadius: '20px',
                  minHeight: { xs: 'auto', md: '600px' },
                }}
              >
                {/* Left Side - Text Content */}
                <Grid
                  item
                  xs={12}
                  md={7}
                  sx={{
                    position: 'relative',
                    zIndex: 2,
                    padding: { xs: 3, md: 8 },
                  }}
                >
                  <Typography
                    component="h1"
                    variant="h2"
                    color={primaryColor}
                    sx={{
                      marginBottom: '20px',
                      fontFamily: 'Urbanist',
                      ...styles.titleSize,
                      fontWeight: 900,
                    }}
                  >
                    Welsh Athletics & RouteBuddies
                  </Typography>
                  <div
                    style={{
                      width: '100%',
                      height: '10px',
                      backgroundColor: primaryColor,
                      marginTop: 20,
                      marginBottom: 30,
                      maxWidth: 400,
                    }}
                  />
                  <Typography
                    variant={'body1'}
                    style={{
                      marginBottom: 20,
                      fontFamily: 'Urbanist',
                      fontSize: 20,
                      letterSpacing: 1.3,
                      color: 'rgba(40,40,40,1)',
                      lineHeight: 1.8,
                    }}
                  >
                    We're thrilled to announce our partnership with{' '}
                    <strong>Welsh Athletics</strong> to bring safe, inclusive
                    spaces for every run club across Wales.
                  </Typography>

                  <Typography
                    variant={'body1'}
                    style={{
                      marginBottom: 20,
                      fontFamily: 'Urbanist',
                      fontSize: 20,
                      letterSpacing: 1.3,
                      color: 'rgba(40,40,40,1)',
                      lineHeight: 1.8,
                    }}
                  >
                    Together, we're committed to creating a community where
                    runners of all abilities can connect, stay safe, and enjoy
                    their passion for running. Through RouteBuddies, Welsh
                    Athletics clubs now have access to cutting-edge safety
                    features, group coordination tools, and a vibrant community
                    platform.
                  </Typography>

                  <Typography
                    variant={'body1'}
                    style={{
                      marginBottom: 30,
                      fontFamily: 'Urbanist',
                      fontSize: 20,
                      letterSpacing: 1.3,
                      color: 'rgba(40,40,40,1)',
                      lineHeight: 1.8,
                    }}
                  >
                    Whether you're organizing a weekly club run or joining a
                    local group for the first time, RouteBuddies makes it easier
                    and safer than ever to connect with fellow runners.
                  </Typography>

                  {/* Buttons Section */}
                  <Grid container spacing={2} sx={{ marginTop: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        style={{
                          padding: 20,
                          borderRadius: '100px',
                          backgroundColor: primaryColor,
                          color: 'white',
                        }}
                        onClick={scrollToBenefits}
                      >
                        <Typography
                          style={{
                            color: 'white',
                            fontFamily: 'Urbanist',
                            fontWeight: '700',
                            textTransform: 'none',
                            fontSize: 18,
                          }}
                        >
                          Learn More
                        </Typography>
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        fullWidth
                        style={{
                          padding: 20,
                          borderRadius: '100px',
                          backgroundColor: 'white',
                          color: primaryColor,
                          border: `2px solid ${primaryColor}`,
                        }}
                        onClick={() => {
                          navigate('/welsh-athletics/claim');
                        }}
                      >
                        <Typography
                          style={{
                            color: primaryColor,
                            fontFamily: 'Urbanist',
                            fontWeight: '700',
                            textTransform: 'none',
                            fontSize: 18,
                          }}
                        >
                          Claim Your Club Space
                        </Typography>
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right Side - Image Space */}
                <Grid
                  xs={12}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    top: 0,
                    width: '50%',
                    zIndex: 1,
                  }}
                >
                  <img
                    className="bgImage"
                    src="/static/home_app_main.png"
                    alt="RouteBuddies App"
                  />
                </Grid>
              </Grid>
            </Grid>
          </FadeInComponent>
        </Grid>

        {/* Benefits Section */}
        <Grid
          id="benefits-section"
          container
          item
          xs={12}
          justifyContent={'center'}
          alignContent={'center'}
          style={{
            backgroundColor: primaryColor,
            paddingTop: 60,
            paddingBottom: 60,
          }}
        >
          <FadeInComponent scrollPosition={scrollPosition}>
            <Grid item container xs={10} md={8} justifyContent={'center'}>
              <Typography
                component="h2"
                variant="h3"
                color="white"
                sx={{
                  marginBottom: '40px',
                  fontFamily: 'Urbanist',
                  fontWeight: 900,
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                What This Means for Your Club
              </Typography>

              <Grid container spacing={4} justifyContent={'center'}>
                <Grid item xs={12} md={4} display={'flex'}>
                  <div
                    style={{
                      backgroundColor: 'white',
                      padding: 30,
                      borderRadius: '15px',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: 'Urbanist',
                        fontSize: 24,
                        fontWeight: '700',
                        color: primaryColor,
                        marginBottom: 15,
                        textAlign: 'center',
                      }}
                    >
                      Safe Spaces
                    </Typography>
                    <Typography
                      style={{
                        fontFamily: 'Urbanist',
                        fontSize: 18,
                        color: 'rgba(40,40,40,1)',
                        lineHeight: 1.6,
                        textAlign: 'center',
                      }}
                    >
                      ID-verified communities ensure secure, inclusive spaces
                      for all runners with our state-of-the-art safety features.
                    </Typography>
                  </div>
                </Grid>

                <Grid item xs={12} md={4} display={'flex'}>
                  <div
                    style={{
                      backgroundColor: 'white',
                      padding: 30,
                      borderRadius: '15px',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: 'Urbanist',
                        fontSize: 24,
                        fontWeight: '700',
                        color: primaryColor,
                        marginBottom: 15,
                        textAlign: 'center',
                      }}
                    >
                      Easy Coordination
                    </Typography>
                    <Typography
                      style={{
                        fontFamily: 'Urbanist',
                        fontSize: 18,
                        color: 'rgba(40,40,40,1)',
                        lineHeight: 1.6,
                        textAlign: 'center',
                      }}
                    >
                      Organize group runs, track routes together, and
                      communicate seamlessly with in-app messaging.
                    </Typography>
                  </div>
                </Grid>

                <Grid item xs={12} md={4} display={'flex'}>
                  <div
                    style={{
                      backgroundColor: 'white',
                      padding: 30,
                      borderRadius: '15px',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: 'Urbanist',
                        fontSize: 24,
                        fontWeight: '700',
                        color: primaryColor,
                        marginBottom: 15,
                        textAlign: 'center',
                      }}
                    >
                      Community Safety
                    </Typography>
                    <Typography
                      style={{
                        fontFamily: 'Urbanist',
                        fontSize: 18,
                        color: 'rgba(40,40,40,1)',
                        lineHeight: 1.6,
                        textAlign: 'center',
                      }}
                    >
                      State-of-the-art SOS tools, automatic check-ins, live
                      location sharing, and more keep your runners safe on every
                      adventure.
                    </Typography>
                  </div>
                </Grid>
              </Grid>

              {/* Call to Action Buttons */}
              <Grid
                container
                spacing={2}
                justifyContent={'center'}
                sx={{ marginTop: 6 }}
              >
                <Grid item xs={12} sm={5} md={4}>
                  <Button
                    fullWidth
                    style={{
                      padding: 20,
                      borderRadius: '100px',
                      backgroundColor: 'white',
                      color: primaryColor,
                    }}
                    onClick={() => {
                      window.location.href =
                        'https://routebuddies.com/?appTrigger=true';
                    }}
                  >
                    <Typography
                      style={{
                        color: primaryColor,
                        fontFamily: 'Urbanist',
                        fontWeight: '700',
                        textTransform: 'none',
                        fontSize: 18,
                      }}
                    >
                      Open in App
                    </Typography>
                  </Button>
                </Grid>
                <Grid item xs={12} sm={5} md={4}>
                  <Button
                    fullWidth
                    style={{
                      padding: 20,
                      borderRadius: '100px',
                      backgroundColor: 'transparent',
                      color: 'white',
                      border: '2px solid white',
                    }}
                    onClick={() => {
                      navigate('/welsh-athletics/claim');
                    }}
                  >
                    <Typography
                      style={{
                        color: 'white',
                        fontFamily: 'Urbanist',
                        fontWeight: '700',
                        textTransform: 'none',
                        fontSize: 18,
                      }}
                    >
                      Claim Your Club Space
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </FadeInComponent>
        </Grid>
      </Grid>
    </>
  );
}
